import React, { useState, useContext, useRef, useEffect } from 'react';
import { StoreContext } from '../../store';
import './index.css';

import { ReactComponent as Instragram } from '../../assets/social-icons/white-instagram.svg';
import { ReactComponent as LinkedIn } from '../../assets/social-icons/white-linkedin.svg';
import { ReactComponent as Twitter } from '../../assets/social-icons/white-twitterx.svg';
import { ReactComponent as Phone } from '../../assets/phone-calling.svg';
import { ReactComponent as Envelope } from '../../assets/envelope.svg';
import { ReactComponent as Check } from '../../assets/survey/check-circle.svg';

import Select from 'react-select';

import FormError from '../../utility/FormError.jsx';
import Show from '../../utility/Show.jsx';

import { isValidEmail, getLSData, setLSData } from '../../services';
import { sendEmail } from '../../api.js';
import { getContactEmailTemplate } from '../../templates/email';

/* ---------------------------------
 * Named export: ContactForm (self-contained)
 * --------------------------------- */
export const ContactForm = ({ inFooter=false, onSubmitted }) => {
  const { state, dispatch, actions } = useContext(StoreContext);
  const { errorMessage, fromPricingPage } = state;

  const [selectedTier, setSelectedTier] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    if (fromPricingPage) {
      const tier = getLSData('pricing-tier');
      setSelectedTier(tier);
    }
  }, [isMounted, fromPricingPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const firstName = formRef.current[0].value;
    const lastName  = formRef.current[1].value;
    const email     = formRef.current[2].value;
    const phone     = formRef.current[3].value;
    const tier      = selectedTier ? selectedTier.toUpperCase() : '';
    const message   = formRef.current[fromPricingPage ? 6 : 4].value;

    if (!firstName || !lastName || !email || !phone || !message || (fromPricingPage && !tier)) {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: 'Please fill in all fields.' });
      return;
    }

    if (!isValidEmail(email)) {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: 'Please enter a valid email address.' });
      return;
    }

    actions.clearErrorMessage();

    const payload = {
      email: 'bcalinao@walkspan.com',
      subject: 'New Contact Form Submission',
      text: '',
      html: getContactEmailTemplate(firstName, lastName, email, phone, fromPricingPage ? tier : null, message),
    };

    try {
      await sendEmail(payload);
      actions.sendDataToGoogleAnalytics('sent_feedback', {});
      formRef.current.reset();
      setSubmitted(true);
      onSubmitted?.(true);
    } catch (error) {
      console.log(error);
      onSubmitted?.(false);
    } finally {
      dispatch({ type: 'SET_FROM_PRICING_PAGE', payload: false });
      setLSData('pricing-tier', null);
      setSelectedTier(null);
    }
  };

  return (
    <div className="ContactUs justify-center items-center gap-8 mx-auto px-8 text-black ">
      {/* Left: contact info — now included in the form component */}
      <div className="contact-info">
        <h1 className="font-medium text-2xl lg:text-4xl">LET'S WORK TOGETHER!</h1>
        <p className="message !text-xl">
          We're always looking for new opportunities and partners—local and global. Schedule a meeting!
        </p>

        <div className="contact-details">
          <h2 className='!text-xl'>Contact Detail</h2>
          <div className="ml-0 md:ml-4 !text-lg">
            <p>
              <span role="img" aria-label="email">
                <Envelope />
              </span>{' '}
              Info@walkspan.com
            </p>
            <p>
              <span role="img" aria-label="phone">
                <Phone />
              </span>{' '}
              914-419-0667
            </p>
          </div>
        </div>

        <div className="social-media">
          <h2>Connect with us!</h2>
          <div className="links">
            <a target="_blank" rel="noreferrer" href="https://www.instagram.com/walkspan_nyc/">
              <Instragram />
            </a>
            <a target="_blank" rel="noreferrer" href="https://www.linkedin.com/company/walkspan-inc">
              <LinkedIn />
            </a>
            <a target="_blank" rel="noreferrer" href="https://twitter.com/walkspan_walks">
              <Twitter />
            </a>
          </div>
        </div>
      </div>

      {/* Right: the form */}
      <div className="contact-form">
        <Show.When isTrue={submitted}>
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <Check className="w-12 h-12 text-green-500" />
            <h2 className="text-xl font-semibold">Thank you for reaching out!</h2>
            <p className="text-base">We've received your message and will get back to you soon.</p>
            <button
              className="submit-again-btn !w-fit text-sm bg-[#f1592a] border-none cursor-pointer p-0 !px-6"
              onClick={() => setSubmitted(false)}
            >
              Submit another message
            </button>
          </div>
        </Show.When>

        <Show.When isTrue={!submitted}>
          <Show.When isTrue={!inFooter}>
            <div className="flex justify-center relative text-white text-lg font-medium py-2 mb-4">
              PROVIDE YOUR DETAILS BELOW
            </div>
          </Show.When>
          <form ref={formRef}>
            <div className="form-group">
              <input type="text" name="first_name" placeholder="First name" />
              <input type="text" name="last_name" placeholder="Last name" />
            </div>

            <div className="form-group">
              <input type="email" name="email" placeholder="E-mail" />
            </div>

            <div className="form-group">
              <input type="text" name="phone" placeholder="Phone" />
            </div>

            <Show.When isTrue={fromPricingPage}>
              <div className="form-group">
                <Select
                  name="tier"
                  options={[
                    { value: 'Enterprise', label: 'Enterprise' },
                    { value: 'Premium', label: 'Premium' },
                  ]}
                  placeholder="Select Tier"
                  value={selectedTier ? { value: selectedTier, label: selectedTier.toUpperCase() } : null}
                  onChange={(option) => setSelectedTier(option ? option.value : null)}
                  className="contact-tier-select-container"
                  classNamePrefix="contact-tier-select"
                />
              </div>
            </Show.When>

            <div className="form-group">
              <textarea name="message" placeholder="Message"></textarea>
            </div>

            <Show.When isTrue={errorMessage}>
              <FormError error={errorMessage} />
            </Show.When>

            <button onClick={handleSubmit}>Submit</button>
          </form>
        </Show.When>
      </div>
    </div>
  );
};

const ContactUs = () => {
  const { state, navigate } = useContext(StoreContext);
  const { fromPricingPage } = state;

  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = `Contact Us - Walkspan`;
  }, []);

  const gotoHome = () => {
    setIsSubmitted(false);
    navigate('/');
  };

  return (
    <div className="mx-auto">
      <div className="bg-[#1c293a] text-white text-3xl text-center py-12">
        <div className="px-4">
          {fromPricingPage ? 'Thank you, We will be in touch shortly' : 'BOOK A DEMO'}
        </div>
      </div>

      {/* Single self-contained component */}
      {/* <ContactForm onSubmitted={setIsSubmitted} /> */}

      <Show.When isTrue={isSubmitted}>
        <div className="flex gap-2 ty-noif mt-2 p-2 border-[1px] border-solid border-black rounded-lg mx-8">
          <Check /> Thank you for reaching out to us. We will get back to you soon.
        </div>

        {/* Optional CTA back to home */}
        <div className="mt-3 mx-8">
          <button onClick={gotoHome}>Back to Home</button>
        </div>
      </Show.When>
    </div>
  );
};

export default ContactUs;
